import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import * as XLSX from "xlsx";
import AdminService from "../../../api/services/adminService";
import {
  StatusBadge,
  formatDate,
  formatNum,
  getSubscriptionId,
  stakeReportRowToExport,
  userFullName,
} from "./stakeReportShared";
import "../Home/Homepage.css";
import "../Trading/AllOpenOrdersPage.css";
import "./StakingStakesPage.css";
import "./StakingFullReportPage.css";

/** Backend hard-caps limit at 20 — always send 20 for list. */
const PAGE_LIMIT = 20;
const STATUS_OPTIONS = ["", "ACTIVE", "COMPLETED", "CANCELLED"];
const EXCEL_SHEET_TITLE = "Stake Report";

const StakingFullReportPage = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(null);
  const [hasNext, setHasNext] = useState(false);
  const [hasPrev, setHasPrev] = useState(false);
  const [reportSummary, setReportSummary] = useState(null);

  const [userSearch, setUserSearch] = useState("");
  const [debouncedUser, setDebouncedUser] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [currencyId, setCurrencyId] = useState("");
  const [planId, setPlanId] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const [exportLoading, setExportLoading] = useState(false);
  const [exportMessage, setExportMessage] = useState({ type: "", text: "" });

  useEffect(() => {
    const t = setTimeout(() => setDebouncedUser(userSearch.trim()), 800);
    return () => clearTimeout(t);
  }, [userSearch]);

  useEffect(() => {
    setPage(1);
  }, [debouncedUser, statusFilter, currencyId, planId, from, to]);

  const buildParams = useCallback(
    (pageNum) => {
      const params = { page: pageNum, limit: PAGE_LIMIT };
      if (statusFilter) params.status = statusFilter;
      if (debouncedUser) {
        params.user = debouncedUser;
        params.search = debouncedUser;
      }
      if (currencyId.trim()) params.currency_id = currencyId.trim();
      if (planId.trim()) params.plan_id = planId.trim();
      if (from) params.from = from;
      if (to) params.to = to;
      return params;
    },
    [debouncedUser, statusFilter, currencyId, planId, from, to]
  );

  useEffect(() => {
    const fetchReport = async () => {
      setLoading(true);
      setError("");
      try {
        const res = await AdminService.getStakingFullReport(buildParams(page));
        const list = Array.isArray(res?.data) ? res.data : [];
        setRows(list);

        const pag = res?.pagination || {};
        const pages = Math.max(1, Number(pag.total_pages) || 1);
        setTotalPages(pages);
        setTotalCount(typeof pag.total === "number" ? pag.total : null);
        setHasNext(
          typeof pag.has_next === "boolean" ? pag.has_next : page < pages
        );
        setHasPrev(
          typeof pag.has_prev === "boolean" ? pag.has_prev : page > 1
        );
        setReportSummary(res?.summary ?? null);

        if (res?.success === false && !list.length) {
          setError(res?.message || "Failed to load stake report.");
        }
      } catch (err) {
        setRows([]);
        setError(
          err?.response?.data?.message || err?.message || "Failed to load stake report."
        );
      } finally {
        setLoading(false);
      }
    };
    fetchReport();
  }, [buildParams, page]);

  const fetchAllPagesForExport = async () => {
    let currentPage = 1;
    let lastPage = 1;
    const all = [];

    do {
      const res = await AdminService.getStakingFullReport(buildParams(currentPage));
      if (res?.success === false && !Array.isArray(res?.data)) {
        throw new Error(res?.message || "Failed to fetch data for export.");
      }
      const list = Array.isArray(res?.data) ? res.data : [];
      all.push(...list);
      lastPage = res?.pagination?.total_pages || 1;
      currentPage += 1;
    } while (currentPage <= lastPage);

    return all;
  };

  const handleExportExcel = async () => {
    setExportLoading(true);
    setExportMessage({ type: "", text: "" });
    try {
      const allRows = await fetchAllPagesForExport();
      if (!allRows.length) {
        setExportMessage({
          type: "error",
          text: "No stake reports match your filters to export.",
        });
        return;
      }

      const worksheet = XLSX.utils.json_to_sheet(allRows.map(stakeReportRowToExport));
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, EXCEL_SHEET_TITLE);

      const dateStamp = new Date().toISOString().slice(0, 10);
      XLSX.writeFile(workbook, `stake_report_${dateStamp}.xlsx`);

      setExportMessage({
        type: "success",
        text: `Exported ${allRows.length} row(s) to Excel (summary fields; open View for full history).`,
      });
      setTimeout(() => setExportMessage({ type: "", text: "" }), 5000);
    } catch (err) {
      setExportMessage({
        type: "error",
        text: err?.message || "Failed to export Excel file.",
      });
    } finally {
      setExportLoading(false);
    }
  };

  const pageStart = rows.length === 0 ? 0 : (page - 1) * PAGE_LIMIT + 1;
  const pageEnd = rows.length === 0 ? 0 : (page - 1) * PAGE_LIMIT + rows.length;

  return (
    <main className="dashboard-main dashboard-subpage">
      <section className="dashboard-hero">
        <div className="dashboard-hero__text">
          <p className="dashboard-hero__breadcrumb">
            <Link to="/dashboard/homepage">Dashboard</Link>
            <span style={{ margin: "0 0.35rem" }}>/</span>
            <span>Staking</span>
            <span style={{ margin: "0 0.35rem" }}>/</span>
            <span>Stake Report</span>
          </p>
          <h1 className="dashboard-hero__title">Stake Report</h1>
          <p className="dashboard-hero__subtitle">
            Paginated list (max 20). Open View for full ROI / referral history.
          </p>
        </div>
        <div className="dashboard-hero__icon">
          <i className="fas fa-file-alt" />
        </div>
      </section>

      <div className="dashboard-body">
        {reportSummary ? (
          <div className="stake-report-summary">
            <div className="stake-report-summary__card">
              <span className="stake-report-summary__value">
                {formatNum(reportSummary.total_subscriptions)}
              </span>
              <span className="stake-report-summary__label">Total subscriptions</span>
            </div>
            <div className="stake-report-summary__card">
              <span className="stake-report-summary__value">
                {formatNum(reportSummary.total_invested)}
              </span>
              <span className="stake-report-summary__label">Invested (this page)</span>
            </div>
            <div className="stake-report-summary__card">
              <span className="stake-report-summary__value">
                {formatNum(reportSummary.total_roi_paid)}
              </span>
              <span className="stake-report-summary__label">ROI paid (this page)</span>
            </div>
            <div className="stake-report-summary__card">
              <span className="stake-report-summary__value">
                {formatNum(reportSummary.total_referral_paid)}
              </span>
              <span className="stake-report-summary__label">Referral paid (this page)</span>
            </div>
          </div>
        ) : null}

        <section className="openorders-section">
          <div className="openorders-section__header">
            <div className="openorders-section__title-wrap">
              <span className="openorders-section__icon">
                <i className="fas fa-list" />
              </span>
              <h2 className="openorders-section__title">Stakers</h2>
            </div>
            <div className="openorders-section__stats staking-stakes__header-actions">
              <span className="openorders-stat">
                <strong>{rows.length}</strong> on page
                {totalCount != null && (
                  <>
                    {" "}
                    · <strong>{totalCount}</strong> total
                  </>
                )}
              </span>
              {totalPages > 1 && (
                <span className="openorders-stat">
                  Page <strong>{page}</strong> / <strong>{totalPages}</strong>
                </span>
              )}
              <button
                type="button"
                className="staking-stakes__export-btn"
                onClick={handleExportExcel}
                disabled={exportLoading || loading}
                title="Download filtered stake report as Excel (summary)"
              >
                <i className={`fas ${exportLoading ? "fa-spinner fa-spin" : "fa-file-excel"}`} />
                {exportLoading ? "Exporting…" : "Download Excel"}
              </button>
            </div>
          </div>

          {error ? (
            <p style={{ color: "#dc2626", padding: "0 0.5rem 0.75rem", margin: 0 }}>{error}</p>
          ) : null}

          {exportMessage.text ? (
            <p
              style={{
                color: exportMessage.type === "error" ? "#dc2626" : "#166534",
                padding: "0 0.5rem 0.75rem",
                margin: 0,
                background:
                  exportMessage.type === "error"
                    ? "rgba(220, 38, 38, 0.08)"
                    : "rgba(22, 101, 52, 0.08)",
                borderRadius: 8,
              }}
              role="status"
            >
              {exportMessage.text}
            </p>
          ) : null}

          <div
            className="openorders-search-wrap"
            style={{ flexWrap: "wrap", gap: "0.5rem", alignItems: "flex-end" }}
          >
            <input
              type="search"
              placeholder="User (email / UUID / phone / name)"
              value={userSearch}
              onChange={(e) => setUserSearch(e.target.value)}
              className="openorders-search-input"
              style={{ minWidth: 220, flex: "1 1 220px" }}
            />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="openorders-search-input"
              style={{ flex: "0 1 160px" }}
            >
              {STATUS_OPTIONS.map((s) => (
                <option key={s || "all"} value={s}>
                  {s || "All statuses"}
                </option>
              ))}
            </select>
            <input
              type="text"
              placeholder="Currency ObjectId"
              value={currencyId}
              onChange={(e) => setCurrencyId(e.target.value)}
              className="openorders-search-input"
              style={{ minWidth: 140, flex: "0 1 180px" }}
            />
            <input
              type="text"
              placeholder="Plan ObjectId"
              value={planId}
              onChange={(e) => setPlanId(e.target.value)}
              className="openorders-search-input"
              style={{ minWidth: 140, flex: "0 1 180px" }}
            />
            <label className="staking-page-limit">
              <span>From</span>
              <input
                type="date"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                className="openorders-search-input"
              />
            </label>
            <label className="staking-page-limit">
              <span>To</span>
              <input
                type="date"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                className="openorders-search-input"
              />
            </label>
            <span className="openorders-stat" style={{ margin: 0, fontSize: "0.82rem" }}>
              Page size fixed at <strong>{PAGE_LIMIT}</strong>
            </span>
          </div>

          {loading ? (
            <div className="openorders-empty">
              <i className="fas fa-spinner fa-spin" /> Loading…
            </div>
          ) : rows.length > 0 ? (
            <>
              <div className="openorders-table-wrap">
                <table className="openorders-table">
                  <thead>
                    <tr>
                      <th>Subscription ID</th>
                      <th>UUID</th>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Package</th>
                      <th>Invested</th>
                      <th>ROI paid</th>
                      <th>Referral paid</th>
                      <th>Status</th>
                      <th>Start</th>
                      <th>End</th>
                      <th style={{ width: "1%", whiteSpace: "nowrap" }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => {
                      const staker = row.staker || {};
                      const staking = row.staking || {};
                      const pkg = row.package || {};
                      const summary = row.summary || {};
                      const packageCurrency =
                        pkg.currency ?? pkg.currency_short_name ?? staking.currency ?? "—";
                      const durationDays =
                        pkg.duration_days ?? staking.duration_days ?? null;
                      const subscriptionId = getSubscriptionId(row);
                      const rowKey = subscriptionId ?? `${staker.id}-${row.created_at}`;
                      const roiPaid =
                        row.roi_payouts?.total_paid ??
                        summary.total_roi_paid_to_staker ??
                        staking.total_roi_paid;
                      const referralPaid =
                        row.referral_payouts?.total_distributed ??
                        summary.total_referral_commission_paid ??
                        staking.referral_commission_total_paid;

                      return (
                        <tr key={rowKey}>
                          <td>
                            <code style={{ fontSize: "0.8rem", wordBreak: "break-all" }}>
                              {subscriptionId ?? "—"}
                            </code>
                          </td>
                          <td>
                            {staker.id ? (
                              <Link
                                to={`/dashboard/trader/${staker.id}`}
                                style={{
                                  color: "#dc2626",
                                  fontWeight: 600,
                                  textDecoration: "none",
                                }}
                              >
                                {staker.uuid ?? "—"}
                              </Link>
                            ) : (
                              staker.uuid ?? "—"
                            )}
                          </td>
                          <td>{userFullName(staker)}</td>
                          <td>{staker.email ?? "—"}</td>
                          <td>
                            <span style={{ fontWeight: 600 }}>{packageCurrency}</span>
                            {durationDays != null ? (
                              <span
                                style={{
                                  display: "block",
                                  fontSize: "0.78rem",
                                  opacity: 0.75,
                                }}
                              >
                                {durationDays} days
                                {pkg.return_percentage != null
                                  ? ` · ${pkg.return_percentage}%`
                                  : ""}
                              </span>
                            ) : null}
                          </td>
                          <td>
                            {formatNum(staking.invested_amount ?? summary.invested_amount)}
                          </td>
                          <td>{formatNum(roiPaid)}</td>
                          <td>{formatNum(referralPaid)}</td>
                          <td>
                            <StatusBadge status={row.status} />
                          </td>
                          <td>{formatDate(staking.start_date)}</td>
                          <td>{formatDate(staking.end_date)}</td>
                          <td>
                            <Link
                              to={`/dashboard/staking/stake-report/${subscriptionId}`}
                              className="staking-stakes__view-btn"
                              style={{ textDecoration: "none" }}
                            >
                              <i className="fas fa-eye" /> View
                            </Link>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div
                className="staking-stakes__pagination"
                style={{
                  padding: "0.75rem 0",
                  display: "flex",
                  alignItems: "center",
                  gap: "0.75rem",
                  flexWrap: "wrap",
                }}
              >
                <button
                  type="button"
                  className="cp-btn cp-btn--outline"
                  style={{ margin: 0 }}
                  disabled={!hasPrev || loading || page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  Previous
                </button>
                <span className="openorders-stat" style={{ margin: 0 }}>
                  Showing {pageStart}–{pageEnd}
                  {totalCount != null ? ` of ${totalCount}` : ""}
                  {totalPages > 1 ? ` · Page ${page} of ${totalPages}` : ""}
                </span>
                <button
                  type="button"
                  className="cp-btn cp-btn--outline"
                  style={{ margin: 0 }}
                  disabled={!hasNext || loading || page >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                </button>
              </div>
            </>
          ) : (
            <p className="openorders-empty">No stake reports match your filters.</p>
          )}
        </section>
      </div>
    </main>
  );
};

export default StakingFullReportPage;
